import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EXPIRY_WARNING_DAYS, expiryState } from '@/features/vault/ExpiryField';
import { credentialsApi, type CredentialType } from '@/api/credentials';
import {
  credentialSchemasApi,
  primarySecretKey,
  type CredentialTypeSchemaResponse,
} from '@/api/credentialSchemas';
import { useAuthStore } from '@/stores/auth';
import VaultDetailModal from '@/features/vault/VaultDetail';
import { CRED_TYPES } from '@/features/vault/CredentialForm';
import { ChevronRightIcon, FolderIcon, StarIcon } from '@/features/notes/icons';
import FilterChip from '@/components/ui/FilterChip';
import FilterPopover, { type ModifiedFilter } from '@/features/vault/FilterPopover';
import { tagsApi, type TagResponse } from '@/api/tags';
import { CredentialRow } from '@/features/vault/CredentialRow';
import { CreateForm } from '@/features/vault/CreateForm';
import { SearchIcon } from '@/features/vault/listIcons';

// ChevronRightIcon + FolderIcon — imports neutilizate imediat, păstrate pt
// posibile upgrade-uri ulterioare.
void ChevronRightIcon;
void FolderIcon;

export default function VaultList() {
  const { t } = useTranslation();
  const tenantId = useAuthStore((s) => s.currentTenantId);
  const keyPair = useAuthStore((s) => s.keyPair);
  const queryClient = useQueryClient();

  const [showForm, setShowForm] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterTypes, setFilterTypes] = useState<Set<CredentialType>>(new Set());
  const [filterModified, setFilterModified] = useState<ModifiedFilter>('all');
  const [params, setParams] = useSearchParams();

  // Deep-link din CommandPalette: /vault?open=<id>
  useEffect(() => {
    const openParam = params.get('open');
    if (openParam && openParam !== openId) {
      setOpenId(openParam);
      const next = new URLSearchParams(params);
      next.delete('open');
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const list = useQuery({
    queryKey: ['credentials', tenantId],
    queryFn: () => credentialsApi.list(tenantId!),
    enabled: !!tenantId,
  });

  const tagsQuery = useQuery({
    queryKey: ['tags', tenantId],
    queryFn: () => tagsApi.list(tenantId!),
    enabled: !!tenantId,
    staleTime: 60_000,
  });
  // Reminder de expirare: calculat din lista deja încărcată, fără cerere în plus.
  const expiry = useMemo(() => {
    let expired = 0;
    let soon = 0;
    for (const c of list.data ?? []) {
      const state = expiryState(c.expires_at);
      if (state?.kind === 'expired') expired++;
      else if (state?.kind === 'soon') soon++;
    }
    return { expired, soon };
  }, [list.data]);

  const tagsById = useMemo(() => {
    const m: Record<string, TagResponse> = {};
    for (const t of tagsQuery.data ?? []) m[t.id] = t;
    return m;
  }, [tagsQuery.data]);

  // Schemas fetch one-shot; cache-uit per tenant.
  const schemas = useQuery({
    queryKey: ['credential-schemas', tenantId],
    queryFn: () => credentialSchemasApi.list(tenantId!),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });
  const schemasByType = useMemo(() => {
    const m: Record<string, CredentialTypeSchemaResponse> = {};
    for (const s of schemas.data ?? []) m[s.type] = s;
    return m;
  }, [schemas.data]);

  const modifiedCutoff = useMemo(() => {
    const now = Date.now();
    switch (filterModified) {
      case 'today':
        return now - 24 * 60 * 60 * 1000;
      case 'week':
        return now - 7 * 24 * 60 * 60 * 1000;
      case 'month':
        return now - 30 * 24 * 60 * 60 * 1000;
      default:
        return null;
    }
  }, [filterModified]);

  const filtered = useMemo(() => {
    if (!list.data) return list.data;
    const q = search.trim().toLowerCase();
    return list.data.filter((c) => {
      if (filterTypes.size > 0 && !filterTypes.has(c.type)) return false;
      if (modifiedCutoff !== null) {
        const updatedMs = new Date(c.updated_at).getTime();
        if (updatedMs < modifiedCutoff) return false;
      }
      if (!q) return true;
      const haystack = [
        c.title,
        c.username,
        c.url,
        ...Object.values(c.meta ?? {}).map((v) => (v == null ? '' : String(v))),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [list.data, search, filterTypes, modifiedCutoff]);

  const activeFilterCount =
    filterTypes.size + (filterModified !== 'all' ? 1 : 0);

  const clearAllFilters = () => {
    setFilterTypes(new Set());
    setFilterModified('all');
  };

  const modifiedLabel: Record<ModifiedFilter, string> = {
    all: '',
    today: t('vault_ui.list.modified_today'),
    week: t('vault_ui.list.modified_week'),
    month: t('vault_ui.list.modified_month'),
  };
  const typeLabelOf = (type: CredentialType) => {
    const def = CRED_TYPES.find((x) => x.value === type);
    return def ? t(def.labelKey) : type;
  };

  const pinnedCreds = useMemo(
    () => (filtered ?? []).filter((c) => c.pinned),
    [filtered],
  );
  const unpinnedCreds = useMemo(
    () => (filtered ?? []).filter((c) => !c.pinned),
    [filtered],
  );

  const pinMutation = useMutation({
    mutationFn: ({ id, pin }: { id: string; pin: boolean }) =>
      pin ? credentialsApi.pin(id) : credentialsApi.unpin(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credentials', tenantId] });
    },
  });

  if (!tenantId || !keyPair) {
    return <p className="text-sm text-[var(--text-muted)]">{t('vault.no_tenant')}</p>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="display-title">{t('vault.title')}</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            {t('vault.subtitle')}
          </p>
        </div>
        <button className="btn-primary" onClick={() => setShowForm((v) => !v)}>
          {showForm ? t('common.cancel') : t('vault.new_credential')}
        </button>
      </div>

      {showForm && (
        <CreateForm
          tenantId={tenantId}
          ownerPublicKey={keyPair.publicKey}
          schemasByType={schemasByType}
          onDone={() => {
            setShowForm(false);
            queryClient.invalidateQueries({ queryKey: ['credentials', tenantId] });
          }}
        />
      )}

      {list.data && list.data.length > 0 && (
        <div className="space-y-2 mb-4">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <input
                className="input pl-9"
                type="search"
                placeholder={t('vault.search_placeholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <span
                className="absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: 'var(--text-dim)' }}
              >
                <SearchIcon />
              </span>
            </div>
            <FilterPopover
              modified={filterModified}
              onModifiedChange={setFilterModified}
              types={filterTypes}
              onTypesChange={setFilterTypes}
              activeCount={activeFilterCount}
              onClearAll={clearAllFilters}
            />
          </div>
          {activeFilterCount > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              {filterModified !== 'all' && (
                <FilterChip
                  label={modifiedLabel[filterModified]}
                  onClear={() => setFilterModified('all')}
                />
              )}
              {Array.from(filterTypes).map((t) => (
                <FilterChip
                  key={t}
                  label={typeLabelOf(t)}
                  onClear={() => {
                    const next = new Set(filterTypes);
                    next.delete(t);
                    setFilterTypes(next);
                  }}
                />
              ))}
              <button
                type="button"
                onClick={clearAllFilters}
                className="text-[11px] hover:underline"
                style={{ color: 'var(--text-dim)' }}
              >
                {t('vault_ui.filter.clear_all')}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="card">
        {list.isLoading && <p className="text-sm text-[var(--text-muted)]">{t('common.loading')}</p>}
        {list.isError && (
          <p className="text-sm text-red-600">
            {t('vault_ui.list.load_error', { message: String(list.error) })}
          </p>
        )}
        {(expiry.expired > 0 || expiry.soon > 0) && (
          <div
            className="text-xs rounded px-3 py-2 mb-3"
            data-testid="expiry-banner"
            style={{
              color: 'var(--text)',
              background: expiry.expired > 0 ? 'var(--danger-bg)' : 'var(--bg-amber)',
              border: `1px solid ${expiry.expired > 0 ? 'var(--danger)' : 'var(--c-amber)'}`,
            }}
          >
            <span className="font-medium">
              {t('vault.expiry_banner', {
                expired: expiry.expired,
                soon: expiry.soon,
                days: EXPIRY_WARNING_DAYS,
              })}
            </span>{' '}
            <span className="text-[var(--text-muted)]">{t('vault.expiry_banner_hint')}</span>
          </div>
        )}
        {list.data && list.data.length === 0 && (
          <p className="text-sm text-[var(--text-muted)] text-center py-8">
            {t('vault.empty')}
          </p>
        )}
        {list.data && list.data.length > 0 && filtered && filtered.length === 0 && (
          <p className="text-sm text-[var(--text-muted)] text-center py-8">
            {t('vault.no_match')}
          </p>
        )}
        {filtered && filtered.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-[var(--text-muted)] dark:text-[var(--text-dim)] uppercase border-b border-[var(--border-strong)]">
                <th className="pb-2 font-medium w-6"></th>
                <th className="pb-2 font-medium">{t('vault.col_title')}</th>
                <th className="pb-2 font-medium">{t('vault.col_type')}</th>
                <th className="pb-2 font-medium">{t('vault.col_username')}</th>
                <th className="pb-2 font-medium">{t('vault.col_primary_secret')}</th>
                <th className="pb-2 font-medium">{t('vault.col_updated')}</th>
                <th className="pb-2"></th>
              </tr>
            </thead>
            <tbody>
              {pinnedCreds.length > 0 && (
                <>
                  <tr>
                    <td
                      colSpan={7}
                      className="pt-3 pb-1 text-[10px] uppercase tracking-wider font-semibold"
                      style={{ color: 'var(--text-dim)' }}
                    >
                      <span className="inline-flex items-center gap-1">
                        <StarIcon filled className="w-3 h-3 text-amber-500" />
                        {t('vault.pinned')}
                      </span>
                    </td>
                  </tr>
                  {pinnedCreds.map((c) => (
                    <CredentialRow
                      key={c.id}
                      summary={c}
                      keyPair={keyPair}
                      secretKey={primarySecretKey(schemasByType[c.type])}
                      tagsById={tagsById}
                      onOpen={() => setOpenId(c.id)}
                      onTogglePin={() =>
                        pinMutation.mutate({ id: c.id, pin: !c.pinned })
                      }
                    />
                  ))}
                  {unpinnedCreds.length > 0 && (
                    <tr>
                      <td
                        colSpan={7}
                        className="pt-4 pb-1 text-[10px] uppercase tracking-wider font-semibold"
                        style={{ color: 'var(--text-dim)' }}
                      >
                        {t('vault.all')}
                      </td>
                    </tr>
                  )}
                </>
              )}
              {unpinnedCreds.map((c) => (
                <CredentialRow
                  key={c.id}
                  summary={c}
                  keyPair={keyPair}
                  secretKey={primarySecretKey(schemasByType[c.type])}
                  tagsById={tagsById}
                  onOpen={() => setOpenId(c.id)}
                  onTogglePin={() =>
                    pinMutation.mutate({ id: c.id, pin: !c.pinned })
                  }
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openId && (
        <VaultDetailModal
          id={openId}
          onClose={() => setOpenId(null)}
          onDeleted={() =>
            queryClient.invalidateQueries({ queryKey: ['credentials', tenantId] })
          }
          keyPair={keyPair}
        />
      )}
    </div>
  );
}
